/*
 * Copyright 2022 Siemens
 */

import {Communication} from './communication';
import {Settings} from './settings';
import {Subject, type SubjectInterface} from '../shared/observer';
import App from '../App';

export interface DatastoreItem {
  id: string;
  name: string;
  desc?: string;
  status: string;
  result?: string;
  statusState?: 'default' | 'error' | 'success' | 'warning' | 'hyperlink' | undefined;
  dataFiles?: ReadonlyArray<string>;
  selectedDataFile?: string;
  image?: string;
  imageId?: string;
  nodeId?: string;
  execId?: string;
  environment?: Record<string, string>;
  experiment?: string;
  experimentId?: string;
  selectedExperimentDataFile?: string;
  parameterset?: string;
  parametersetId?: string;
  selectedParametersetDataFile?: string;
  startTime?: string;
  endTime?: string;
  extraResultFiles?: string;

  custom?: unknown;
}

export interface ResourceOption {
  cluster: ReadonlyArray<{
    id: string;
    name: string;
    description?: string;
    limit: number;
  }>;
  node: ReadonlyArray<{
    id: string;
    name: string;
    description?: string;
  }>;
  image: ReadonlyArray<{
    id: string;
    name: string;
    prescan: string;
    description?: string;
    shared: boolean;
  }>;
}

export interface ResourceStatus {
  display: string;
  total: number;
  status: Array<{id: string; label: string; value: number; color: string}>;
}

export class Datastores {
  static readonly #symbol = Symbol('Datastores');

  static readonly #resourceOption = new Subject<ResourceOption | null>(null);
  static readonly resourceOption: SubjectInterface<ResourceOption | null> = this.#resourceOption;

  static readonly EXPERIMENT = Symbol('Experiment');
  static readonly #experimentItems = new Subject<Array<DatastoreItem> | null>(null);
  static readonly experimentItems: SubjectInterface<ReadonlyArray<DatastoreItem> | null> = this.#experimentItems;
  static readonly PARAMETER_SET = Symbol('ParameterSet');
  static readonly #parameterSetItems = new Subject<Array<DatastoreItem> | null>(null);
  static readonly parameterSetItems: SubjectInterface<ReadonlyArray<DatastoreItem> | null> = this.#parameterSetItems;
  static readonly JOB = Symbol('Job');
  static readonly #jobItems = new Subject<Array<DatastoreItem> | null>(null);
  static readonly jobItems: SubjectInterface<ReadonlyArray<DatastoreItem> | null> = this.#jobItems;

  static readonly #deltaStatus = new Subject(false);
  static readonly deltaStatus: SubjectInterface<boolean> = this.#deltaStatus;

  static readonly #defaultStatus = JSON.stringify([
    {
      display: 'Experiments',
      total: 0,
      status: [
        {
          id: 'new',
          label: 'New',
          value: 0,
          color: '#8c8c8c',
        },
        {
          id: 'ready',
          label: 'Ready',
          value: 0,
          color: '#0f789b',
        },
        {
          id: 'queued',
          label: 'Queued',
          value: 0,
          color: '#f0f0f0',
        },
        {
          id: 'terminate',
          label: 'Terminate',
          value: 0,
          color: '#ba83a5',
        },
        {
          id: 'terminating',
          label: 'Terminating',
          value: 0,
          color: '#7f4664',
        },
        {
          id: 'terminated',
          label: 'Terminated',
          value: 0,
          color: '#512d43',
        },
        {
          id: 'building',
          label: 'Building',
          value: 0,
          color: '#ffb900',
        },
        {
          id: 'failed',
          label: 'Failed',
          value: 0,
          color: '#bf1b1b',
        },
        {
          id: 'built',
          label: 'Built',
          value: 0,
          color: '#2a932a',
        },
      ],
    },
    {
      display: 'Parameter Sets',
      total: 0,
      status: [
        {
          id: 'new',
          label: 'New',
          value: 0,
          color: '#8c8c8c',
        },
        {
          id: 'ready',
          label: 'Ready',
          value: 0,
          color: '#0f789b',
        },
      ],
    },
    {
      display: 'Jobs',
      total: 0,
      status: [
        {
          id: 'new',
          label: 'New',
          value: 0,
          color: '#8c8c8c',
        },
        {
          id: 'ready',
          label: 'Ready',
          value: 0,
          color: '#0f789b',
        },
        {
          id: 'queued',
          label: 'Queued',
          value: 0,
          color: '#f0f0f0',
        },
        {
          id: 'terminate',
          label: 'Terminate',
          value: 0,
          color: '#ba83a5',
        },
        {
          id: 'terminating',
          label: 'Terminating',
          value: 0,
          color: '#7f4664',
        },
        {
          id: 'terminated',
          label: 'Terminated',
          value: 0,
          color: '#512d43',
        },
        {
          id: 'running',
          label: 'Running',
          value: 0,
          color: '#ffb900',
        },
        {
          id: 'failed',
          label: 'Failed',
          value: 0,
          color: '#bf1b1b',
        },
        {
          id: 'finished',
          label: 'Finished',
          value: 0,
          color: '#2a932a',
        },
      ],
    },
  ]);
  static readonly #resourceStatus = new Subject<Array<ResourceStatus>>(JSON.parse(this.#defaultStatus));
  static readonly resourceStatus: SubjectInterface<ReadonlyArray<ResourceStatus>> = this.#resourceStatus;

  static #websocket: WebSocket | undefined;

  static #workspace: {name: string; id?: string} | undefined;

  static init(): void {
    Settings.currentWorkspace.addObserver(this.#symbol, (workspace) => {
      if (this.#workspace !== undefined && this.#workspace.name === workspace.name && this.#workspace.id === workspace.id) {
        console.debug('Skip workspace update as there are no changes detected', JSON.parse(JSON.stringify(this.#workspace)));
        return;
      }
      this.#workspace = workspace;
      this.reset();
    });
  }

  static #storeType(
    store: string,
  ): (typeof Datastores)['EXPERIMENT'] | (typeof Datastores)['PARAMETER_SET'] | (typeof Datastores)['JOB'] | null {
    switch (`${store}`.toLowerCase()) {
      case 'exp':
      case 'experiment':
      case 'experiments':
        return Datastores.EXPERIMENT;
      case 'pms':
      case 'parameter':
      case 'parameters':
      case 'parameterset':
      case 'parametersets':
        return Datastores.PARAMETER_SET;
      case 'job':
      case 'jobs':
        return Datastores.JOB;
      default:
        return null;
    }
  }

  static #statusType(status: string) {
    switch (status) {
      case 'failed':
        return 'error';
      case 'terminate':
      case 'terminating':
      case 'terminated':
        return 'warning';
      case 'ready':
      case 'built':
      case 'finished':
        return 'success';
      case 'new':
        return 'hyperlink';
      default:
        return 'default';
    }
  }

  static reset(): void {
    this.#clearDatastore();
    this.#deltaTerminate();
    setTimeout(() => {
      this.#loadOption();
      if (this.#workspace === undefined) return;
      const workspace = this.#workspace.id === undefined || this.#workspace.id === '' ? this.#workspace.name : this.#workspace.id;
      this.#deltaProcessor(workspace);
      this.#loadDatastore(workspace);
    });
  }

  static reloadDatastore(): void {
    this.#clearDatastore();
    setTimeout(() => {
      this.#loadOption();
      if (this.#workspace === undefined) return;
      const workspace = this.#workspace.id === undefined || this.#workspace.id === '' ? this.#workspace.name : this.#workspace.id;
      this.#loadDatastore(workspace);
    });
  }

  static reloadOption(): void {
    this.#resourceOption.dispatchUnique(null);
    this.#loadOption();
  }

  static #clearDatastore(): void {
    this.#experimentItems.dispatchUnique(null);
    this.#parameterSetItems.dispatchUnique(null);
    this.#jobItems.dispatchUnique(null);
    this.#resourceStatus.dispatchUnique(JSON.parse(this.#defaultStatus));
  }

  static #loadOption(): void {
    Communication.fetch<ResourceOption>('option', 'GET', 'json+object').then(
      (options) => {
        if (options.status === 'ok' && options.data !== null) this.#resourceOption.dispatch(options.data);
        else if (options.status === 'ok') App.toast('warn', 'Datastores', undefined, `Data of 'option' is invalid`);
        else
          App.toast(
            'error',
            'Datastores',
            undefined,
            `Loading of 'option' failed: ${
              ('error' in options && options.error.message) ||
              `[Code: ${options.code}] ${('data' in options && options.data) || 'Unknown'}`
            }`,
          );
      },
      (err) => {
        App.toast(
          'error',
          'Datastores',
          undefined,
          `Loading of 'option' failed: ${(err instanceof Error && err.message) || err}`,
        );
      },
    );
  }

  static #computeStatus(): void {
    const newStatus = JSON.parse(this.#defaultStatus) as Array<ResourceStatus>;
    if (this.experimentItems.value) {
      const statusTarget = newStatus[0]!;
      statusTarget.total = this.experimentItems.value.length;
      for (const item of this.experimentItems.value) {
        const statusCounter = statusTarget.status.find((status) => status.id === item.status);
        if (statusCounter) statusCounter.value++;
      }
    }
    if (this.parameterSetItems.value) {
      const statusTarget = newStatus[1]!;
      statusTarget.total = this.parameterSetItems.value.length;
      for (const item of this.parameterSetItems.value) {
        const statusCounter = statusTarget.status.find((status) => status.id === item.status);
        if (statusCounter) statusCounter.value++;
      }
    }
    if (this.jobItems.value) {
      const statusTarget = newStatus[2]!;
      statusTarget.total = this.jobItems.value.length;
      for (const item of this.jobItems.value) {
        const statusCounter = statusTarget.status.find((status) => status.id === item.status);
        if (statusCounter) statusCounter.value++;
      }
    }
    this.#resourceStatus.dispatchUnique(newStatus);
  }

  static #loadDatastore(workspace: string): void {
    Communication.fetch<{type: string; resources: Array<DatastoreItem>}[]>(
      {workspace, isDatastores: true},
      'GET',
      'json+array',
    ).then(
      (store) => {
        if (store.status === 'ok' && store.data !== null)
          for (const data of store.data) {
            const symbol = this.#storeType(data.type);
            for (const res of data.resources) res.statusState = this.#statusType(res.status);
            if (symbol === Datastores.EXPERIMENT) this.#experimentItems.dispatch(data.resources);
            else if (symbol === Datastores.PARAMETER_SET) this.#parameterSetItems.dispatch(data.resources);
            else this.#jobItems.dispatch(data.resources);
            this.#computeStatus();
          }
        else if (store.status === 'ok') App.toast('warn', 'Datastores', undefined, `Data of 'datastores' is invalid`);
        else
          App.toast(
            'error',
            'Datastores',
            undefined,
            `Loading of 'datastores' failed: ${
              ('error' in store && store.error.message) ||
              `[Code: ${store.code}] ${('data' in store && store.data) || 'Unknown'}`
            }`,
          );
      },
      (err) => {
        App.toast(
          'error',
          'Datastores',
          undefined,
          `Loading of 'datastores' failed: ${(err instanceof Error && err.message) || err}`,
        );
      },
    );
  }

  static #deltaTerminate(): void {
    if (this.#websocket !== undefined && this.#websocket.readyState !== WebSocket.CLOSED) {
      this.#websocket.close();
      this.#websocket = undefined;
    }
  }

  static #deltaProcessor(workspace: string): void {
    if (this.#websocket !== undefined && this.#websocket.readyState !== WebSocket.CLOSED) {
      this.#websocket.close();
    }

    this.#websocket = new WebSocket(Communication.websocketAPI(workspace));
    const localReferenceWebSocket = this.#websocket;
    this.#websocket.onmessage = (event) => this.#deltaUpdate(event, JSON.parse(event.data));
    this.#websocket.onopen = () => {
      if (localReferenceWebSocket === this.#websocket) {
        console.debug('Delta processor, started');
        Datastores.#deltaStatus.dispatchUnique(true);
      } else localReferenceWebSocket.close();
    };
    this.#websocket.onerror = (event) => {
      console.error('Delta processor', event);
      App.toast('warn', 'Delta processor', undefined, 'Delta processor error, restarting delta processor');
      if (localReferenceWebSocket === this.#websocket) this.#deltaProcessor(workspace);
    };
    this.#websocket.onclose = (event) => {
      console.debug('Delta processor, closed', event.code, event.reason, event.wasClean);
      if (localReferenceWebSocket === this.#websocket) {
        Datastores.#deltaStatus.dispatchUnique(false);
        switch (
          event.code // https://www.rfc-editor.org/rfc/rfc6455.html#section-7.4.1
        ) {
          case 1000: // As-intended
          case 1001: // Graceful server shutdown or browser navigating away
          case 1002: // Protocol error
          case 1003: // Invalid message type
          case 1005: // Unknown/missing closure code
          case 1010: // Not compatible
          case 1015: // Connection error (TLS?)
            break;
          case 1006: // Abnormal termination, timeout?
          case 1007: // Invalid message payload
          case 1008: // Invalid policy
          case 1009: // Invalid payload size
          case 1011: // Unable to process
            this.#deltaProcessor(workspace);
            break;
          default:
            break;
        }
      }
    };
  }

  static #deltaUpdate(
    event: MessageEvent,
    delta: {class?: 'datastore'; type?: string; evt: 'changed' | 'removed'; data: DatastoreItem},
  ): void {
    if (delta.class === 'datastore') {
      const store = this.#storeType(`${delta.type}`);
      let datastore: Subject<Array<DatastoreItem> | null> | undefined;
      if (store === Datastores.EXPERIMENT) datastore = this.#experimentItems;
      else if (store === Datastores.PARAMETER_SET) datastore = this.#parameterSetItems;
      else if (store === Datastores.JOB) datastore = this.#jobItems;
      if (datastore === undefined)
        App.toast('warn', 'Datastores', undefined, `Delta ignored as datastore type is invalid: ${delta.type}`);
      else if (datastore.value === null)
        console.warn('Delta ignored as datastore is still loading', delta.type, JSON.parse(JSON.stringify(delta.data)));
      else {
        delta.data.statusState = this.#statusType(delta.data.status);
        const found = datastore.value.findIndex((val) => val.id === delta.data.id);
        if (found === -1) {
          if (delta.evt === 'changed')
            datastore.dispatch([...datastore.value, delta.data].sort((a, b) => a.name.localeCompare(b.name)));
          else console.debug('Delta removal ignored as element is not found', JSON.parse(JSON.stringify(delta.data)));
        } else {
          if (delta.evt === 'changed') {
            datastore.value.splice(found, 1, delta.data);
            datastore.dispatch([...datastore.value].sort((a, b) => a.name.localeCompare(b.name)));
          } else {
            datastore.value.splice(found, 1);
            datastore.dispatch([...datastore.value].sort((a, b) => a.name.localeCompare(b.name)));
          }
        }
        this.#computeStatus();
      }
    } else console.info('Unknown delta event', event, delta); // TODO: Other delta types, log...
  }
}
